import * as d3 from 'd3'

import { margin } from '@aytm/app/stats/page/question-views/vk/components/chart/lib/constants'
import { countLabelSize, initScaleProjection } from '@aytm/app/stats/page/question-views/vk/components/chart/lib//utils'
import {
  axisGridFormater,
  formatterXaxis,
  formatterYaxis,
  getAreas,
  getBars,
  getFreqPeriod,
  getGraph1,
  getGraph2,
  getPoint,
  getPoints,
  getXExtrem,
  getXMaximum,
  getXMinimum,
  getYbyX,
  getYExtrem,
  getYMaximum,
  valueFormatter
} from '@aytm/app/stats/page/question-views/vk/components/chart/lib/vankonan-utils'

const drawDefs = container => {
  const defs = container.append('defs')

  const arrow = defs
    .append('marker')
    .attr('id', 'arrow')
    .attr('viewBox', '0 0 20 10')
    .attr('refX', 5)
    .attr('refY', 5)
    .attr('markerUnits', 'strokeWidth')
    .attr('orient', 'auto')
    .attr('markerWidth', 16)
    .attr('markerHeight', 8)

  arrow.append('polyline').attr('points', '0,0 20,5 0,10 5,5').attr('fill', '#d8d9d9')

  const arrowUp = defs
    .append('marker')
    .attr('id', 'arrow-up')
    .attr('viewBox', '0 0 20 10')
    .attr('refX', 5)
    .attr('refY', 5)
    .attr('markerUnits', 'strokeWidth')
    .attr('markerWidth', 16)
    .attr('markerHeight', 32)

  arrowUp
    .append('polyline')
    .attr('points', '0,0 20,5 0,10 5,5')
    .attr('fill', '#d8d9d9')
    .attr('transform', 'translate(0, 16) rotate(-90)')

  const grad = defs
    .append('linearGradient')
    .attr('id', 'gray-back-grad')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', 1)

  grad.append('stop').attr('offset', 0).attr('stop-color', '#4b4c4e').attr('stop-opacity', 1)

  grad.append('stop').attr('offset', 1).attr('stop-color', '#1a1a1a').attr('stop-opacity', 1)

  return defs
}

const drawBarsAndLines = ({ root, area, line, opts }) => {
  const { model } = opts

  const bars = getBars({ model })

  bars.forEach(bar => {
    root
      .append('g')
      .append('path')
      .datum(bar)
      .attr('d', area)
      .attr('fill', '#6a8493')
      .attr('opacity', 0.6)
      .attr('stroke', 'rgba(100,100,100,0.7)')
      .attr('stroke-width', 1)
  })

  root
    .append('g')
    .append('path')
    .classed('graph1', true)
    .datum(getGraph1(model))
    .attr('d', line)
    .attr('stroke', 'steelblue')
    .attr('stroke-width', 2)
    .attr('fill', 'none')

  root
    .append('g')
    .append('path')
    .classed('graph2', true)
    .datum(getGraph2(model))
    .attr('d', line)
    .style('stroke', 'steelblue')
    .style('stroke-width', 2)
    .style('fill', 'none')
}

const baseTextAttrs = sel =>
  sel.attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial').attr('font-size', 10).style('line-height', '16px')

const drawAxis = (root, opts) => {
  const { model, width, height, axisWidth, axisHeight, infographic } = opts

  const background = model?.backgroundColor
  const panel = model?.panel || false

  if (panel) {
    root.append('rect').attr('x', 0).attr('y', 0).attr('width', margin.x).attr('height', height).attr('fill', '#384854')

    root
      .append('rect')
      .attr('x', 0)
      .attr('y', height - 20)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#384854')
  }

  const xScale = d3
    .scaleLinear()
    .domain(getXExtrem(model))
    .range([margin.x, margin.x + axisWidth])

  const yScale = d3.scaleLinear().domain(getYExtrem(model)).range([0, axisHeight])

  const xMax = getXMaximum(model)
  const yMax = getYMaximum(model)

  const xAxis = d3
    .axisBottom(xScale)
    .tickFormat(d =>
      formatterXaxis(model, {
        xMax,
        value: parseInt(String(d), 10)
      })
    )
    .tickSize(0)

  const yRaw = model.yRaw || false
  const yAxis = d3
    .axisLeft(yScale)
    .tickFormat(d => (yRaw ? String(d) : formatterYaxis(model, { yMax, value: d })))
    .tickSize(0)

  const xa = root
    .append('g')
    .attr('class', 'axis-lines x-axis')
    .attr('transform', `translate(${margin.x}, ${height - 20})`)
    .call(xAxis)

  const ya = root
    .append('g')
    .attr('class', 'axis-lines y-axis')
    .attr('transform', `translate(${margin.x}, ${margin.y})`)
    .call(yAxis)

  const isDark = background === '#4c5a65'

  if (!infographic) {
    xa.select('path').attr('stroke', '#d8d9d9').attr('marker-end', 'url(#arrow)')
    baseTextAttrs(xa.selectAll('text')).attr('dy', '1em').attr('transform', `translate(-${margin.x}, 0)`)

    ya.select('path').attr('stroke', '#d8d9d9').attr('marker-start', 'url(#arrow-up)')
    baseTextAttrs(ya.selectAll('text')).attr('dx', '-2em')

    if (isDark) {
      xa.selectAll('text').attr('fill', '#FFFFFF')
      ya.selectAll('text').attr('fill', '#FFFFFF')
    }
  } else {
    baseTextAttrs(xa.selectAll('text'))
      .attr('dy', '1em')
      .attr('transform', `translate(-${margin.x}, 0)`)
      .attr('fill', '#FFFFFF')

    baseTextAttrs(ya.selectAll('text')).attr('dx', '-2em').attr('fill', '#FFFFFF')
  }

  xa.selectAll('g.tick')
    .append('line')
    .attr('class', 'grid-line')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', -height)
    .attr('stroke', '#d8d9d9')
    .attr('stroke-width', 1)
    .attr('opacity', d => axisGridFormater(model, { axis: 'x', value: d }))
    .attr('transform', `translate(-${margin.x}, 0)`)

  ya.selectAll('g.tick')
    .append('line')
    .attr('class', 'grid-line')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', width)
    .attr('y2', 0)
    .attr('stroke', '#d8d9d9')
    .attr('stroke-width', 1)
    .attr('opacity', d => (yRaw ? 0.2 : axisGridFormater(model, { axis: 'y', value: d })))

  xa.select('path.domain').attr('transform', `translate(-${margin.x}, 0)`)

  xa.append('text')
    .attr('x', width - 125)
    .attr('y', -10)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', 10)
    .attr('fill', isDark ? '#ffffff' : '#9d9fa2')
    .style('line-height', '16px')
    .text(model.xText || 'UNIT PRICE')

  const yLabelWidth = 80
  ya.append('text')
    .attr('x', margin.y)
    .attr('y', 0)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', 10)
    .attr('fill', isDark ? '#ffffff' : '#9d9fa2')
    .attr('transform', `translate(-10, ${yLabelWidth / 4}) rotate(270)`)
    .style('line-height', '16px')
    .text(model?.yText || '')

  root
    .append('line')
    .attr('class', 'axis-lines')
    .attr('x1', xScale(getXMinimum(model)))
    .attr('y1', yScale(0) + margin.y)
    .attr('x2', xScale(getXMaximum(model)))
    .attr('y2', yScale(0) + margin.y)
    .attr('stroke', '#d8d9d9')
    .attr('stroke-width', 3)

  root
    .append('line')
    .attr('class', `slide-line slide-line-${model.position}`)
    .attr('x1', -100)
    .attr('y1', 0)
    .attr('x2', -100)
    // down to the zero line, which sits a top margin below the scale's own zero, as the axis line above does
    .attr('y2', infographic ? yScale(0) + margin.y : height - 20)
    .attr('stroke', '#d8d9d9')
    .attr('stroke-width', 1)
    .attr('opacity', 0.5)

  if (infographic) {
    root.selectAll('.domain').style('display', 'none')
  }
}

const drawPoints = ({ root, xScale, yScale, opts }) => {
  const scaleProjection = initScaleProjection({ xScale, yScale })
  const { model, palette, infographic } = opts

  const points = getPoints(model)

  points.forEach(point => {
    const { x, y, n, i } = point
    const color = palette[i % palette.length]

    const ptX = scaleProjection(x).x
    const ptY = scaleProjection(y).y
    const zeroY = scaleProjection(0).y

    const group = root.append('g')

    group
      .append('circle')
      .attr('cx', ptX)
      .attr('cy', ptY)
      .attr('r', infographic ? 0 : 4)
      .attr('fill', color)

    if (y !== 0 || (n === 'break_even' && infographic)) {
      group
        .append('line')
        .attr('x1', ptX)
        .attr('y1', ptY)
        .attr('x2', ptX)
        .attr('y2', zeroY)
        .attr('stroke', color)
        .attr('stroke-width', 2)

      const [, minY] = getYExtrem(model)

      group
        .append('line')
        .classed('axis-lines', true)
        .attr('x1', ptX)
        .attr('y1', scaleProjection(minY).y)
        .attr('x2', ptX)
        .attr('y2', zeroY)
        .attr('stroke-width', 1)
        .attr('stroke', '#d8d9d9')
        .attr('opacity', model.gridOpacity)
    }
  })
}

const drawAreas = ({ root, area, opts }) => {
  const { model } = opts

  const areas = getAreas(model)

  areas.forEach(_area => {
    root.append('g').append('path').datum(_area).attr('d', area).style('stroke-width', 0).style('fill', '#6a8493')
  })
}
const drawBasicLabel = ({ root, opts, scaleProjection, point, name, raw = false, labels }) => {
  if (!point.prop) return null

  const { model, width, palette } = opts

  const position = model?.position
  const labelSize = countLabelSize(
    `<span style='font-weight: 600'>${point.value}</span> - <span style='font-weight: lighter'>${point.text}: ${point.prop}</span>`
  )

  const px = raw ? point.x : scaleProjection(point.x).x
  const py = raw ? point.y : scaleProjection(point.y).y
  const reverse = px + labelSize.width > width - 4
  // the callout stands three lines above its point; a point too near the chart's top edge would push
  // it past the edge, where it is cut, so there it hangs below the point instead
  const inverse = py - labelSize.height * 3 < 0

  d3.select(`g.${name}_${position}_label`).remove()

  const g = root.append('g').classed(`${name}_${position}_label`, true)

  const dPath = reverse
    ? [
        `M ${px} ${py + (inverse ? 3 : -3)}`,
        `L ${px - 10} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px - labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px - labelSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py}`
      ].join(' ')
    : [
        `M ${px} ${py + (inverse ? 3 : -3)}`,
        `L ${px + 10} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px + labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px + labelSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py}`
      ].join(' ')

  g.append('path')
    .attr('d', dPath)
    .attr('fill', model?.print ? '#333' : 'url(#gray-back-grad)')

  g.append('text')
    .attr('x', reverse ? px - labelSize.width + 20 : px + 20)
    .attr('y', (inverse ? py + labelSize.height * 2 : py - labelSize.height * 2) + 5)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', '17px')
    .attr('fill', '#FFFFFF')
    .style('cursor', 'default')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 17)
    .style('font-weight', 'lighter')
    .style('line-height', '14px')
    .style('fill', '#FFFFFF')
    .text(`${point.value} - ${point.text}: ${point.prop}`)
    .html(
      `<tspan style='font-weight: 600'>${point.value}</tspan> - ` +
        `<tspan style='font-weight: lighter'>${point.text}: ${point.prop}</tspan>`
    )

  g.append('circle')
    .attr('cx', px)
    .attr('cy', py)
    .attr('r', 4)
    .attr('fill', palette[point.i % palette.length])

  g.on('mouseover', function () {
    // @ts-ignore
    d3.select(this).moveToFront()
  })
  g.on('mouseout', function () {
    if (labels?.frontlabel) {
      // @ts-ignore
      d3.select(labels.frontlabel).moveToFront()
    }
  })
  g.on('mousedown', function () {
    if (labels) labels.frontlabel = this
  })

  return g
}

const drawExtendedLabel = ({ root, opts, scaleProjection, point, name, raw = false, labels }) => {
  const { model, width, palette } = opts
  const position = model?.position

  const labelSize = countLabelSize(
    `<span style='font-weight: 600'>${point.value}</span> - <span style='font-weight: lighter'>${point.text}</span>`
  )
  const propSize = countLabelSize(`<span style='font-weight: 600'>${point.prop}</span>`)

  const prevLabelSel = d3.select(`g.max_rev_${position}_label`)
  let inverse = false

  if (prevLabelSel?.node()) {
    const yProjected = raw ? point.y : scaleProjection(point.y).y
    // @ts-ignore
    const delta = yProjected - prevLabelSel.node().getBBox().y
    inverse = delta < labelSize.height * 5
  }

  const px = raw ? point.x : scaleProjection(point.x).x
  const py = raw ? point.y : scaleProjection(point.y).y
  // as for the basic callout: never above the chart's top edge
  if (py - labelSize.height * 3 < 0) inverse = true

  const reverse = px + labelSize.width + propSize.width > width - 4

  d3.select(`g.${name}_${position}_label`).remove()

  const g = root.append('g').classed(`${name}_${position}_label`, true)

  const mainPath = reverse
    ? [
        `M ${px} ${py + (inverse ? 3 : -3)}`,
        `L ${px - 10} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px - labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px - labelSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py}`
      ].join(' ')
    : [
        `M ${px} ${py + (inverse ? 3 : -3)}`,
        `L ${px + 10} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px + labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px + labelSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px} ${py}`
      ].join(' ')

  g.append('path')
    .attr('d', mainPath)
    .attr('fill', model?.print ? '#333' : 'url(#gray-back-grad)')

  g.append('text')
    .attr('x', reverse ? px - labelSize.width + 10 : px + 10)
    .attr('y', (inverse ? py + labelSize.height * 2 : py - labelSize.height * 2) + 5)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', '17')
    .attr('fill', '#FFFFFF')
    .style('cursor', 'default')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 17)
    .style('font-weight', 'lighter')
    .style('line-height', '14px')
    .style('fill', '#FFFFFF')
    .text(`${point.value} - ${point.text}`)
    .html(
      `<tspan style='font-weight: 600'>${point.value}</tspan> - ` +
        `<tspan style='font-weight: lighter'>${point.text}</tspan>`
    )

  const propPath = reverse
    ? [
        `M ${px - labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px - labelSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px - labelSize.width - propSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px - labelSize.width - propSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px - labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`
      ].join(' ')
    : [
        `M ${px + labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px + labelSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px + labelSize.width + propSize.width} ${py + (inverse ? labelSize.height * 3 : -(labelSize.height * 3))}`,
        `L ${px + labelSize.width + propSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`,
        `L ${px + labelSize.width} ${py + (inverse ? labelSize.height : -labelSize.height)}`
      ].join(' ')

  g.append('path')
    .attr('d', propPath)
    .attr('fill', point.negative ? '#f89028' : '#bad746')

  g.append('text')
    .attr('x', reverse ? px - labelSize.width - propSize.width + 10 : px + labelSize.width + 10)
    .attr('y', (inverse ? py + labelSize.height * 2 : py - labelSize.height * 2) + 5)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', '17px')
    .attr('fill', '#000000')
    .style('cursor', 'default')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 17)
    .style('line-height', '14px')
    .style('fill', '#000000')
    .text(`${point.prop}`)
    .html(`<tspan style='font-weight: 600'>${point.prop}</tspan>`)

  g.append('circle')
    .attr('cx', px)
    .attr('cy', py)
    .attr('r', 4)
    .attr('fill', palette[point.i % palette.length])

  g.on('mousedown', function () {
    if (labels) labels.frontlabel = this
  })

  return g
}

const drawLabels = ({ root, opts, scaleProjection, labels, height }) => {
  const { model } = opts
  const { tooltipsFor = [] } = model

  if (tooltipsFor.includes('break_even')) {
    const point = getPoint(model, 'break_even', '_Profit')

    const labelSize = countLabelSize(
      `<span style='font-weight: 600'>${point.value}</span> - <span style='font-weight: lighter'>${point.text}</span>`
    )

    point.x = scaleProjection(point.x).x
    point.y = scaleProjection(point.y).y

    const inverse = point.y + labelSize.height * 3 > height

    const g = root.append('g')
    g.append('path')
      .attr(
        'd',
        [
          `M ${point.x} ${point.y + (inverse ? -2 : 2)}`,
          `L ${point.x + 10} ${point.y + (inverse ? -labelSize.height : labelSize.height)}`,
          `L ${point.x + labelSize.width} ${point.y + (inverse ? -labelSize.height : labelSize.height)}`,
          `L ${point.x + labelSize.width} ${point.y + (inverse ? -(labelSize.height * 3) : labelSize.height * 3)}`,
          `L ${point.x} ${point.y + (inverse ? -(labelSize.height * 3) : labelSize.height * 3)}`,
          `L ${point.x} ${point.y}`
        ].join(' ')
      )
      .attr('fill', '#aeaeae')

    g.append('text')
      .attr('x', point.x + 20)
      .attr('y', point.y + (inverse ? -(labelSize.height * 2) : labelSize.height * 2) + 5)
      .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
      .attr('font-size', '17px')
      .style('cursor', 'default')
      .style('line-height', '14px')
      .style('fill', '#FFFFFF')
      .text(`${point.value} - ${point.text}`)
      .html(
        `<tspan style='font-weight: 600'>${point.value}</tspan> - ` +
          `<tspan style='font-weight: lighter'>${point.text}</tspan>`
      )
  }

  if (tooltipsFor.includes('max_rev') || tooltipsFor.includes('max_prob') || tooltipsFor.includes('max_freq')) {
    tooltipsFor.forEach(name => {
      if (name === 'break_even' || name === 'max_prof') return

      const key = name === 'max_prob' ? '_Probability' : name === 'max_freq' ? '_Frequency' : '_Revenue'

      const point = getPoint(model, name, key)

      drawBasicLabel({
        root,
        opts,
        scaleProjection,
        point,
        name,
        raw: false,
        labels
      })
    })
  }

  if (tooltipsFor.includes('max_prof')) {
    const name = 'max_prof'
    const point = getPoint(model, name, '_Profit')
    drawExtendedLabel({
      root,
      opts,
      scaleProjection,
      point,
      name,
      raw: false,
      labels
    })
  }
}

const initChartEvents = ({ root, opts, scaleProjection, labels, height, eventsOptions }) => {
  const { model, axisWidth } = opts
  const { position, tooltipsFor = [] } = model

  const scaleXPrimitive = d3
    .scaleLinear()
    .domain([margin.x, margin.x + axisWidth])
    .range(getXExtrem(model))

  root.on('mouseleave', function () {
    if (eventsOptions.slidemode) {
      const slider = d3.select(`line.slide-line-${position}`)

      slider.attr('x1', -100).attr('x2', -100)
      drawLabels({ root, opts, scaleProjection, labels, height })
    }
  })

  root
    .on('mousemove', function (event) {
      const coord = d3.pointer(event, this) // [x, y]

      if (coord[0] > margin.x && eventsOptions.slidemode) {
        const slider = d3.select(`line.slide-line-${position}`)
        slider.attr('x1', coord[0]).attr('x2', coord[0])

        if (tooltipsFor.includes('max_rev') || tooltipsFor.includes('max_prob') || tooltipsFor.includes('max_freq')) {
          tooltipsFor.forEach(_p => {
            if (_p === 'break_even' || _p === 'max_prof') return

            const key = _p === 'max_prob' ? '_Probability' : _p === 'max_freq' ? '_Frequency' : '_Revenue'

            const point = getPoint(model, _p, key)

            const _xVal = scaleXPrimitive(coord[0])
            const _yVal = getYbyX(model, _xVal) || 0

            const _value =
              valueFormatter(model, {
                axis: 'x',
                value: _xVal,
                toFixed: true
              }) || `${_xVal.toFixed(2)}`

            const _prop =
              _p === 'max_prob'
                ? `${(parseFloat(_yVal) * 100).toFixed(1)}%`
                : _p === 'max_freq'
                  ? `${_yVal.toFixed(2)}${getFreqPeriod(model)}`
                  : formatterYaxis(model, {
                      value: _yVal,
                      toFixed: true,
                      force: true
                    })

            point.x = coord[0]
            point.y = scaleProjection(_yVal).y
            point.value = _value
            point.prop = _prop
            point.text = point.text.replace('MAX ', '')

            drawBasicLabel({
              root,
              opts,
              scaleProjection,
              point,
              name: _p,
              raw: true,
              labels
            })
          })
        }

        if (tooltipsFor.includes('max_prof')) {
          const point = getPoint(model, 'max_prof', '_Profit')

          const _xVal = scaleXPrimitive(coord[0])
          const _yVal = getYbyX(model, _xVal, 2)

          const _value = valueFormatter(model, { axis: 'x', value: _xVal, toFixed: true }) || `${_xVal.toFixed(2)}`
          const _prop = formatterYaxis(model, {
            value: _yVal,
            toFixed: true,
            force: true
          })

          point.x = coord[0]
          point.y = scaleProjection(_yVal).y
          point.value = _value
          point.prop = _prop
          point.text = point.text.replace('MAX ', '')
          if (_yVal < 0) point.negative = true

          drawExtendedLabel({
            root,
            opts,
            scaleProjection,
            point,
            name: 'max_prof',
            raw: true,
            labels
          })
        }
      }
    })
    .on('mousedown', function () {
      if (eventsOptions.slidemode) {
        const slider = d3.select(`line.slide-line-${position}`)

        slider.attr('x1', -100).attr('x2', -100)
        eventsOptions.slidemode = false
        drawLabels({
          root,
          opts,
          scaleProjection,
          labels,
          height
        })
      } else {
        eventsOptions.slidemode = true
      }
    })
}

export const drawChart = (svgEl, opts) => {
  const { model, width, height, axisWidth, axisHeight } = opts

  const root = d3.select(svgEl)
  const xScale = d3.scaleLinear().domain(getXExtrem(model)).range([0, axisWidth])
  const yScale = d3.scaleLinear().domain(getYExtrem(model)).range([0, axisHeight])
  const scaleProjection = initScaleProjection({ xScale, yScale })

  d3.selection.prototype.moveToFront = function () {
    return this.each(function () {
      if (this.parentNode) {
        this.parentNode.appendChild(this)
      }
    })
  }

  d3.selection.prototype.moveToBack = function () {
    return this.each(function () {
      if (this.parentNode) {
        const firstChild = this.parentNode.firstChild
        if (firstChild) {
          this.parentNode.insertBefore(this, firstChild)
        }
      }
    })
  }

  root.selectAll('*').remove()

  root
    .attr('class', 'axis')
    .attr('version', '1.1')
    .attr('xmlns', 'http://www.w3.org/2000/svg')
    .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)

  root.append('rect').attr('width', '100%').attr('height', '100%').attr('fill', model?.backgroundColor)

  const line = d3
    .line()
    // @ts-ignore
    .x(d => scaleProjection(d.x).x)
    // @ts-ignore
    .y(d => scaleProjection(d.y).y)

  const area = d3
    .area()
    // @ts-ignore
    .x(d => scaleProjection(d.x).x)
    .y0(() => scaleProjection(0).y)
    // @ts-ignore
    .y1(d => scaleProjection(d.y).y)

  drawAxis(root, opts)
  drawDefs(root)
  drawBarsAndLines({ root, opts, line, area })
  drawPoints({ root, opts, xScale, yScale })
  drawAreas({ root, opts, area })

  const labels = {
    frontlabel: null
  }

  const eventsOptions = {
    slidemode: false
  }

  drawLabels({ root, opts, scaleProjection, height, labels })
  initChartEvents({ root, opts, scaleProjection, height, labels, eventsOptions })
}
